import React, { useEffect, useState } from "react";
import { useHomeContext } from './HomeContext';
import { useNavigate, Link } from "react-router-dom";
import { Product } from "./types";
import config from "./config";

type Toast = {
  id: number;
  message: string;
  type: 'success' | 'error';
};

type HomeProps = {
  user: any;
  loginWithRedirect: () => void;
  getAccessTokenSilently: (options?: any) => Promise<string>;
};

const PAGE_SIZE = 50;
const MAX_COMPARE = 3;

const Home: React.FC<HomeProps> = ({ user, loginWithRedirect, getAccessTokenSilently }) => {
  const { products, setProducts, filters, setFilters, page, setPage, compareIds, setCompareIds } = useHomeContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<{ categoryId: number; name: string }[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('name'); // Default sort by name
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setShowAdvancedFilters(false); // Reset on desktop
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    // Auto-remove toast after 4 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  useEffect(() => {
    fetch(`${config.apiBaseUrl}/api/categories`)
      .then(res => res.json())
      .then(data => setCategories(data))
      .catch(() => setCategories([]));

    fetch(`${config.apiBaseUrl}/api/brands`)
      .then(res => res.json())
      .then(data => setBrands(data.map((b: { name: string }) => b.name)))
      .catch(() => setBrands([]));
  }, []);

  const fetchProducts = (filtersObj = filters, pageNum = page) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (filtersObj.name) params.append('name', filtersObj.name);
    if (filtersObj.brand) params.append('brand', filtersObj.brand);
    if (filtersObj.categoryId) params.append('categoryId', filtersObj.categoryId);
    if (filtersObj.gender) params.append('gender', filtersObj.gender);
    if (filtersObj.retailer) params.append('retailer', filtersObj.retailer);
    if (filtersObj.minPrice) params.append('minPrice', filtersObj.minPrice);
    if (filtersObj.maxPrice) params.append('maxPrice', filtersObj.maxPrice);
    if (sortBy) params.append('sortBy', sortBy);
    params.append('skip', ((pageNum - 1) * PAGE_SIZE).toString());
    params.append('take', PAGE_SIZE.toString());

    fetch(`${config.apiBaseUrl}/api/productDB/filter?${params.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch products');
        return res.json();
      })
      .then((data) => {
        setProducts(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'An error occurred');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchProducts(filters, page);
    // eslint-disable-next-line
  }, [page]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchProducts(filters, 1);
  };

  const handlePrev = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNext = () => {
    if (products.length === PAGE_SIZE) setPage(page + 1);
  };

  const handleNotify = async (product: Product) => {
    if (!user) {
      loginWithRedirect();
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${config.apiBaseUrl}/api/notifylist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          productId: product.id,
          userId: user.sub
        })
      });
      addToast(`Product "${product.name}" added to your notify list!`, 'success');
    } catch (err) {
      addToast('Failed to add product to notify list.', 'error');
    }
  };

  const handleCompareChange = (id: number, checked: boolean) => {
    setCompareIds(prev => {
      if (checked) {
        if (prev.length >= MAX_COMPARE) return prev; // Don't add more than 3
        return [...prev, id];
      } else {
        return prev.filter(cid => cid !== id);
      }
    });
  };

  const handleClearComparison = () => {
    setCompareIds([]);
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    setPage(1);
    fetchProducts(filters, 1);
  };

  const sortProducts = (productsToSort: Product[]) => {
    const sorted = [...productsToSort];
    switch (sortBy) {
      case 'priceLowToHigh':
        return sorted.sort((a, b) => {
          const priceA = a.salePrice || a.originalPrice || a.price || 0;
          const priceB = b.salePrice || b.originalPrice || b.price || 0;
          return priceA - priceB;
        });
      case 'priceHighToLow':
        return sorted.sort((a, b) => {
          const priceA = a.salePrice || a.originalPrice || a.price || 0;
          const priceB = b.salePrice || b.originalPrice || b.price || 0;
          return priceB - priceA;
        });
      case 'nameAtoZ':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'nameZtoA':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'brandAtoZ':
        return sorted.sort((a, b) => (a.brand || '').localeCompare(b.brand || ''));
      case 'newest':
        return sorted.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
      case 'discount':
        return sorted.sort((a, b) => {
          const discountA = a.originalPrice && a.salePrice && a.originalPrice > a.salePrice 
            ? ((a.originalPrice - a.salePrice) / a.originalPrice) * 100 : 0;
          const discountB = b.originalPrice && b.salePrice && b.originalPrice > b.salePrice 
            ? ((b.originalPrice - b.salePrice) / b.originalPrice) * 100 : 0;
          return discountB - discountA;
        });
      default:
        return sorted;
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
      {/* <h1 style={{ 
        fontSize: '2.5rem',
        fontWeight: 'bold',
        color: '#2c5aa0',
        marginBottom: '32px',
        textAlign: 'left'
      }}>
        Price Tracker
      </h1> */}

      {/* Filter Form */}
      <div style={{ 
        marginBottom: '32px',
        backgroundColor: '#ffffff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          color: '#2c5aa0',
          marginBottom: '24px',
          textAlign: 'left'
        }}>
          Search & Filter Products
        </h2>
        
        <form onSubmit={handleFilter} style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          alignItems: 'end'
        }}>
          {/* Primary Filters - Always visible */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ 
              marginBottom: '8px', 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#374151',
              textAlign: 'left'
            }}>
              Product Name
            </label>
            <input
              name="name"
              placeholder="Search by name..."
              value={filters.name}
              onChange={handleChange}
              style={{
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: '#fff',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = '#2c5aa0'}
              onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151', textAlign: 'left' }}>Brand</label>
            <select
              name="brand"
              value={filters.brand}
              onChange={handleChange}
              style={{
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                fontSize: '14px',
                backgroundColor: '#fff',
                transition: 'border-color 0.2s',
                cursor: 'pointer'
              }}
              onFocus={(e) => (e.target as HTMLSelectElement).style.borderColor = '#2c5aa0'}
              onBlur={(e) => (e.target as HTMLSelectElement).style.borderColor = '#d1d5db'}
            >
            <option value="">All Brands</option>
            {brands.map((brand) => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </select>
        </div>
        
        {/* Advanced Filters - Show all on desktop, collapsible on mobile */}
        {(!isMobile || showAdvancedFilters) && (
          <>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151', textAlign: 'left' }}>Category</label>
          <select
            name="categoryId"
            value={filters.categoryId}
            onChange={handleChange}
            style={{
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              outline: 'none',
              fontSize: '14px',
              backgroundColor: '#fff',
              transition: 'border-color 0.2s',
              cursor: 'pointer'
            }}
            onFocus={(e) => (e.target as HTMLSelectElement).style.borderColor = '#2c5aa0'}
            onBlur={(e) => (e.target as HTMLSelectElement).style.borderColor = '#d1d5db'}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.categoryId} value={cat.categoryId}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151', textAlign: 'left' }}>Gender</label>
          <select
            name="gender"
            value={filters.gender}
            onChange={handleChange}
            style={{
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              outline: 'none',
              fontSize: '14px',
              backgroundColor: '#fff',
              transition: 'border-color 0.2s',
              cursor: 'pointer'
            }}
            onFocus={(e) => (e.target as HTMLSelectElement).style.borderColor = '#2c5aa0'}
            onBlur={(e) => (e.target as HTMLSelectElement).style.borderColor = '#d1d5db'}
          >
            <option value="">All Genders</option>
            <option value="Men">Men</option>
            <option value="Women">Women</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151', textAlign: 'left' }}>Retailer</label>
          <select
            name="retailer"
            value={filters.retailer}
            onChange={handleChange}
            style={{
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              outline: 'none',
              fontSize: '14px',
              backgroundColor: '#fff',
              transition: 'border-color 0.2s',
              cursor: 'pointer'
            }}
            onFocus={(e) => (e.target as HTMLSelectElement).style.borderColor = '#2c5aa0'}
            onBlur={(e) => (e.target as HTMLSelectElement).style.borderColor = '#d1d5db'}
          >
            <option value="">All Retailers</option>
            <option value="Running Warehouse">Running Warehouse</option>
            <option value="Rebel Sport">Rebel Sport</option>
            <option value="Athletes Foot">Athletes Foot</option>
            <option value="Foot Locker">Foot Locker</option>
            <option value="Nike">Nike</option>
            <option value="RunDNA">Run Dna</option>
            <option value="Jim Kidd Sports">Jim Kidd Sports</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151', textAlign: 'left' }}>Min Price</label>
          <input
            name="minPrice"
            placeholder="Min"
            type="number"
            value={filters.minPrice}
            onChange={handleChange}
            style={{
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              outline: 'none',
              fontSize: '14px',
              backgroundColor: '#fff',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = '#2c5aa0'}
            onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151', textAlign: 'left' }}>Max Price</label>
          <input
            name="maxPrice"
            placeholder="Max"
            type="number"
            value={filters.maxPrice}
            onChange={handleChange}
            style={{
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              outline: 'none',
              fontSize: '14px',
              backgroundColor: '#fff',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = '#2c5aa0'}
            onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
          />
        </div>
        </>
        )}
        
        {/* Toggle Advanced Filters Button (Mobile Only) */}
        {isMobile && (
          <button
            type="button"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#e5e7eb'}
            onMouseOut={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#f3f4f6'}
          >
            {showAdvancedFilters ? '− Hide More Filters' : '+ Show More Filters'}
          </button>
        )}
        
        <button
          type="submit"
          style={{
            alignSelf: isMobile ? 'stretch' : 'flex-end',
            height: 48,
            padding: '12px 24px',
            backgroundColor: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#1d4ed8'}
          onMouseOut={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#2563eb'}
        >
          Filter
        </button>
        </form>
      </div>

      {/* Sort Section */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 12 : 0,
        marginBottom: 24,
        padding: '16px 0',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ fontSize: isMobile ? 14 : 16, color: '#374151' }}>
          {products.length > 0 && `Showing ${products.length} products`}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: isMobile ? '100%' : 'auto' }}>
          <label style={{ fontSize: 14, fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              backgroundColor: '#fff',
              cursor: 'pointer',
              minWidth: isMobile ? 'auto' : 180,
              flex: isMobile ? 1 : 'none'
            }}
          >
            <option value="nameAtoZ">Name (A to Z)</option>
            <option value="nameZtoA">Name (Z to A)</option>
            <option value="priceLowToHigh">Price (Low to High)</option>
            <option value="priceHighToLow">Price (High to Low)</option>
            <option value="brandAtoZ">Brand (A to Z)</option>
            <option value="newest">Recently Updated</option>
            <option value="discount">Highest Discount</option>
          </select>
        </div>
      </div>

      {/* Product Grid */}
      {loading ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          fontSize: '1.2rem',
          color: '#666',
          padding: '40px 20px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}>
            {/* Loading Spinner */}
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #2c5aa0',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            
            {/* Loading Text */}
            <div style={{
              fontSize: '1.1rem',
              fontWeight: '500'
            }}>
              Loading Products...
            </div>
            
            {/* Animated Dots */}
            <div style={{
              fontSize: '1.5rem',
              letterSpacing: '2px',
              animation: 'dots 1.5s infinite'
            }}>
              • • •
            </div>
          </div>
          
          {/* CSS Animation Styles */}
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            @keyframes dots {
              0%, 20% { opacity: 0; }
              50% { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
        </div>
      ) : error ? (
        <div style={{ color: 'red', textAlign: 'center', margin: '2rem 0' }}>{error}</div>
      ) : products.length === 0 ? (
        <div style={{ textAlign: 'center', margin: '2rem 0' }}>No products found.</div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: isMobile ? 16 : 24,
            marginBottom: 32,
          }}
        >
          {sortProducts(products).map((product) => (
            <div
              key={product.id}
              style={{
                background: '#fff',
                color: '#222',
                borderRadius: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%'
              }}
            >
              <Link to={`/product/${product.id}`} style={{ width: '100%', display: 'block', marginBottom: 24 }}>
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 6, display: 'block' }}
                />
              </Link>
              <div style={{ width: '100%', textAlign: 'left' }}>
                <Link to={`/product/${product.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <strong style={{ fontSize: 18, cursor: 'pointer' }}>{product.name}</strong>
                </Link>
                <div style={{ fontSize: 15, margin: '6px 0' }}>
                  {product.brand} {product.model}
                </div>
                {/* <div style={{ fontSize: 14, marginBottom: 6 }}>{product.description}</div> */}
                <div style={{ fontWeight: 'bold', fontSize: 16, margin: '8px 0' }}>
                  {typeof product.originalPrice === 'number' && typeof product.salePrice === 'number' ? (
                    <span>
                      <span style={{ textDecoration: product.originalPrice > product.salePrice ? 'line-through' : 'none', color: product.originalPrice > product.salePrice ? '#888' : undefined }}>
                        <span style={{ fontWeight: 400, marginRight: 4 }}>Original:</span>${product.originalPrice}
                      </span>
                      <span style={{ color: product.originalPrice > product.salePrice ? '#2563eb' : undefined, fontWeight: product.originalPrice > product.salePrice ? 600 : undefined, marginLeft: 12 }}>
                        <span style={{ fontWeight: 400, marginRight: 4 }}>Sale:</span>${product.salePrice}
                      </span>
                      {product.originalPrice > product.salePrice && (
                        <span style={{ color: 'green', fontSize: '13px', marginLeft: 8 }}>
                          Save ${(product.originalPrice - product.salePrice).toFixed(2)}
                          ({Math.round(((product.originalPrice - product.salePrice) / product.originalPrice) * 100)}% off)
                        </span>
                      )}
                    </span>
                  ) : (
                    <>${product.price}</>
                  )}
                </div>
                {product.gender && (
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <b>Gender:</b> {product.gender}
                  </div>
                )}
                {(product.size || product.width) && (
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    {product.size && <div><b>Size:</b> {product.size}</div>}
                    {product.width && <div><b>Width:</b> {product.width}</div>}
                  </div>
                )}
                {product.retailer && (
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <b>Retailer:</b> {product.retailer}
                    {product.retailerSku && <> | <b>SKU:</b> {product.retailerSku}</>}
                  </div>
                )}
              </div>
              <div style={{ width: 'auto',minWidth:'100%', marginTop: 'auto' ,textAlign: 'left'}}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                  Last Updated: {new Date(product.lastUpdated).toLocaleString()}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                  <a
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginRight: 0, fontSize: 14 }}
                  >
                    View Product
                  </a>
                  <Link to={`/product/${product.id}`} className="text-blue-600 hover:underline" style={{ fontSize: 14 }}>
                    View Details
                  </Link>
                </div>
                <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                  <button
                    style={{
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      padding: isMobile ? '8px 16px' : '10px 20px',
                      cursor: 'pointer',
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => handleNotify(product)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-bell" viewBox="0 0 16 16">
                      <path d="M8 0a2 2 0 0 1 2 2v.268c.586.35 1 1 1 1.732v1.5a2 2 0 0 1-1 1.732V14a2 2 0 0 1-4 0V11.5A2 2 0 0 1 3 9.732V8.5c0-.732.414-1.382 1-1.732V2a2 2 0 0 1 2-2zm0 1a1 1 0 0 0-1 1v1.5h2V2a1 1 0 0 0-1-1z"/>
                    </svg>
                    Notify Me
                  </button>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={compareIds.includes(product.id)}
                      onChange={e => handleCompareChange(product.id, e.target.checked)}
                      disabled={!compareIds.includes(product.id) && compareIds.length >= MAX_COMPARE}
                    />
                    Add to Compare
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comparison Section */}
      {compareIds.length >= 1 && (
        <div style={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          backgroundColor: '#fff', 
          borderTop: '2px solid #e5e7eb',
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
          padding: isMobile ? '12px 16px' : '16px 24px',
          zIndex: 1000
        }}>
          <div style={{ 
            maxWidth: 1200, 
            margin: '0 auto',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 12 : 0
          }}>
            <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 600, color: '#374151', marginBottom: isMobile ? 8 : 0 }}>
              {compareIds.length} item{compareIds.length > 1 ? 's' : ''} selected
              {compareIds.length < 2 && !isMobile && <span style={{ fontSize: 14, fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>
                (Select at least 2 items to compare)
              </span>}
            </div>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 8 : 12 }}>
              <button
                style={{
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: isMobile ? '10px 16px' : '12px 24px',
                  cursor: 'pointer',
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                  transition: 'background-color 0.2s'
                }}
                onClick={handleClearComparison}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#dc2626'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = '#ef4444'}
              >
                Clear Comparison List
              </button>
              <button
                style={{
                  backgroundColor: compareIds.length >= 2 ? '#2563eb' : '#9ca3af',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: isMobile ? '10px 16px' : '12px 24px',
                  cursor: compareIds.length >= 2 ? 'pointer' : 'not-allowed',
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                  transition: 'background-color 0.2s'
                }}
                onClick={() => compareIds.length >= 2 && navigate(`/compare?ids=${compareIds.join(",")}`)}
                disabled={compareIds.length < 2}
                onMouseOver={e => {
                  if (compareIds.length >= 2) {
                    e.currentTarget.style.backgroundColor = '#1d4ed8';
                  }
                }}
                onMouseOut={e => {
                  if (compareIds.length >= 2) {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                  }
                }}
              >
                Compare Now ({compareIds.length}/{MAX_COMPARE})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32, gap: 8 }}>
        <button
          onClick={handlePrev}
          disabled={page === 1}
          style={{
            backgroundColor: page === 1 ? '#e0e0e0' : '#2196F3',
            color: page === 1 ? '#999' : 'white',
            border: 'none',
            borderRadius: 4,
            padding: isMobile ? '8px 16px' : '10px 20px',
            cursor: page === 1 ? 'not-allowed' : 'pointer',
            fontSize: 14,
          }}
        >
          {isMobile ? 'Prev' : 'Previous'}
        </button>
        <button
          onClick={handleNext}
          disabled={products.length < PAGE_SIZE}
          style={{
            backgroundColor: products.length < PAGE_SIZE ? '#e0e0e0' : '#2196F3',
            color: products.length < PAGE_SIZE ? '#999' : 'white',
            border: 'none',
            borderRadius: 4,
            padding: isMobile ? '8px 16px' : '10px 20px',
            cursor: products.length < PAGE_SIZE ? 'not-allowed' : 'pointer',
            fontSize: 14,
          }}
        >
          Next
        </button>
      </div>

      {/* Toast Notification Container */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: isMobile ? '90%' : '400px',
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              padding: '14px 18px',
              borderRadius: '8px',
              backgroundColor: toast.type === 'success' ? '#4CAF50' : '#f44336',
              color: 'white',
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              animation: 'slideIn 0.3s ease-out',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {toast.type === 'success' ? (
              <span style={{ fontSize: '18px' }}>✓</span>
            ) : (
              <span style={{ fontSize: '18px' }}>✕</span>
            )}
            {toast.message}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;